import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { PlansHierarchy } from '../plans/PlansHierarchy';
export const WorkOrdersEditModal = ({ isOpen, onClose, maintenancePlan, workOrder, mode = 'edit', onSave }) => {
  // Form state for work order
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    asset_id: '',
    priority: '',
    work_order_type: '',
    scheduled_date: '',
    estimated_hours: '',
  });
  console.log(formData, 'formdata')

  // UI state
  const [isHierarchyOpen, setHierarchyOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [localModalError, setLocalModalError] = useState(null);
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isLoadingAsset, setIsLoadingAsset] = useState(false);
  const [selectedAssetDetails, setSelectedAssetDetails] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const isHydratingRef = useRef(false);

  const formRef = useRef(null);

  // Initialize form when modal opens or workOrder changes
  useEffect(() => {
    if (isOpen && workOrder) {
      isHydratingRef.current = true;
      setIsInitializing(true);
      setLocalModalError(null);
      setFormErrors({});

      if (mode === 'duplicate') {
        // Duplicate mode: load work order data but clear title
        const tasks = workOrder.tasks ? workOrder.tasks.map(task => {
          const {
            assigned_to,
            assignee_name,
            completed_at,
            created_at,
            id,
            observations,
            pause_reasons,
            restart_at,
            seconds_worked,
            status,
            updated_at,
            deleted_at,
            work_order_id,
            ...cleanTask
          } = task;
          return cleanTask;
        }) : [];
        const duplicateFormData = {
          title: '',
          description: workOrder.description || '',
          asset_id: workOrder.asset_id?.toString() || '',
          priority: workOrder.priority || '',
          work_order_type: workOrder.work_order_type || '',
          scheduled_date: workOrder.scheduled_date || new Date().toISOString(),
          estimated_hours: workOrder.estimated_hours || '',
          maintenance_plan_id: workOrder.maintenance_plan_id || '',
          tasks: tasks,
        };
        setFormData(duplicateFormData);
      } else {
        // Edit mode: load work order data
        const editFormData = {
          title: workOrder.title || '',
          description: workOrder.description || '',
          asset_id: workOrder.asset_id?.toString() || '',
          priority: workOrder.priority || '',
          work_order_type: workOrder.work_order_type || '',
          scheduled_date: workOrder.scheduled_date || new Date().toISOString(),
          estimated_hours: workOrder.estimated_hours || '',
        };
        setFormData(editFormData);
      }

      // Mark initialization as complete after state updates are queued
      queueMicrotask(() => {
        isHydratingRef.current = false;
        setIsInitializing(false);
      });
    }
  }, [isOpen, workOrder, mode]);

  // ASSETS ONLY EDITABLE FOR WORK ORDERS WHEN DUPLICATING  
  // Update asset_id when asset is selected from hierarchy
  useEffect(() => {
    if (selectedAssetDetails?.ID) {
      setFormData(prev => ({
        ...prev,
        asset_id: selectedAssetDetails.ID.toString(),
      }));
      setIsUpdating(false);
    }
  }, [selectedAssetDetails]);
  console.log(workOrder, 'WO')

  // Validation
  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.length > 255) {
      errors.title = 'Title must be less than 255 characters';
    }

    if (!formData.asset_id.trim()) {
      errors.asset_id = 'Asset is required';
    }

    if (!formData.priority.trim()) {
      errors.priority = 'Priority is required';
    }

    if (!formData.work_order_type.trim()) {
      errors.work_order_type = 'Work order type is required';
    } else if (!['preventive', 'predictive'].includes(formData.work_order_type)) {
      errors.work_order_type = 'Type must be preventive or predictive';
    }

    if (!formData.estimated_hours.toString().trim()) {
      errors.estimated_hours = 'Estimated hours are required';
    } else if (isNaN(formData.estimated_hours) || parseFloat(formData.estimated_hours) <= 0) {
      errors.estimated_hours = 'Estimated hours must be a positive number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    let eventTime = formData.scheduled_date || '';
    if (!eventTime) {
      eventTime = new Date().toISOString();
    } else {
      // Validate date format
      try {
        const date = new Date(eventTime);
        if (isNaN(date.getTime())) {
          eventTime = new Date().toISOString();
        } else {
          eventTime = date.toISOString();
        }
      } catch {
        eventTime = new Date().toISOString();
      }
    }

    setLocalIsSubmitting(true);
    setLocalModalError(null);


    try {
      const workOrderData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        asset_id: parseInt(formData.asset_id.trim()),
        priority: formData.priority.trim(),
        work_order_type: formData.work_order_type.trim(),
        scheduled_date: eventTime,
        estimated_hours: parseFloat(formData.estimated_hours),
        maintenance_plan_id: formData.maintenance_plan_id,
        tasks: formData.tasks,
      };

      console.log('Work order data to save:', workOrderData);
      await onSave(workOrderData, mode);
      onClose();
    } catch (error) {
      setLocalModalError(error.message || 'Error saving work order');
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLocalIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'title' ? value.slice(0, 255) : value,
    }));
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  if (!isOpen || !workOrder) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center gap-3 justify-center p-4 bg-black/60 backdrop-blur-sm">
      {/* ASSETS ONLY EDITABLE FOR WORK ORDERS WHEN DUPLICATING */}
      {/* Asset hierarchy modal (if open) */
        <div className={`${isHierarchyOpen && mode === 'duplicate' ? 'visible' : 'invisible'} max-h-[85vh] border border-white overflow-y-scroll w-fit scrollbar-thin `}>
          <PlansHierarchy
            assetId={selectedAsset}
            setAssetId={setSelectedAsset}
            setIsLoadingAsset={setIsLoadingAsset}
            setAssetDetails={setSelectedAssetDetails}
            selectMode="asset"
            rootAssetId={maintenancePlan?.asset_id}
          />
        </div>}

      {/* Main modal */}
      <div
        className={`bg-gray-800 ${!isHierarchyOpen ? 'absolute left-1/2 -translate-x-1/2' : ''
          } rounded-2xl border border-gray-700 shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col`}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-white">
            {mode === 'duplicate' ? 'Duplicate Work Order' : 'Edit Work Order'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
            disabled={localIsSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Global error message */}
            {localModalError && (
              <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
                <p className="text-red-300 text-sm">{localModalError}</p>
              </div>
            )}

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
                Work Order Information
              </h3>

              {/* Two-column layout for basic fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Title <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    maxLength="255"
                    value={formData.title}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.title ? 'border-red-500' : 'border-gray-600'
                      }`}
                    placeholder="Work order title"
                    disabled={localIsSubmitting}
                  />
                  {formErrors.title && <p className="mt-1 text-sm text-red-400">{formErrors.title}</p>}
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Priority <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white ${formErrors.priority ? 'border-red-500' : 'border-gray-600'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="" disabled>Select priority</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                  {formErrors.priority && <p className="mt-1 text-sm text-red-400">{formErrors.priority}</p>}
                </div>

                {/* ASSETS ONLY EDITABLE FOR WORK ORDERS WHEN DUPLICATING
                Asset (with hierarchy picker) */
                  mode === 'duplicate' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Asset <span className="text-red-400">*</span>
                      </label>
                      <div className="flex flex-row gap-2">
                        <p
                          className={`w-full px-4 py-2 rounded-lg border bg-gray-700 ${formErrors.asset_id ? 'border-red-500' : 'border-gray-600'
                            } ${formData.asset_id ? 'text-white' : 'text-gray-400'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        >
                          {selectedAssetDetails?.Location
                            ? selectedAssetDetails.Location
                            : isLoadingAsset
                              ? <Loader2 className="w-4 h-4 animate-spin inline" />
                              : formData.asset_id
                                ? workOrder.asset_name
                                : 'Not selected'}
                        </p>
                        <button
                          type="button"
                          onClick={() => setHierarchyOpen(!isHierarchyOpen)}
                          className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors"
                        >
                          {isHierarchyOpen ? 'Close' : 'Open'}
                        </button>
                      </div>
                      {formErrors.asset_id && <p className="mt-1 text-sm text-red-400">{formErrors.asset_id}</p>}
                    </div>)}

                {/* Work Order Type (only preventive/predictive) */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Work Order Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="work_order_type"
                    value={formData.work_order_type}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white ${formErrors.work_order_type ? 'border-red-500' : 'border-gray-600'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="" disabled>Select type</option>
                    <option value="preventive">Preventive</option>
                    <option value="predictive">Predictive</option>
                  </select>
                  {formErrors.work_order_type && <p className="mt-1 text-sm text-red-400">{formErrors.work_order_type}</p>}
                </div>

                {/* Scheduled Date (datetime-local) */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Scheduled Date <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="scheduled_date"
                    value={formData.scheduled_date
                      ? (() => {
                        try {
                          const date = new Date(formData.scheduled_date)
                          if (isNaN(date.getTime())) {
                            return new Date().toISOString().slice(0, 16)
                          }
                          return date.toISOString().slice(0, 16)
                        } catch {
                          return new Date().toISOString().slice(0, 16)
                        }
                      })()
                      : new Date().toISOString().slice(0, 16)
                    }
                    onChange={(e) => {
                      const isoValue = e.target.value ? new Date(e.target.value).toISOString() : new Date().toISOString()
                      setFormData({ ...formData, scheduled_date: isoValue })
                    }}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white ${formErrors.scheduled_date ? 'border-red-500' : 'border-gray-600'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    disabled={localIsSubmitting}
                  />
                  {formErrors.scheduled_date && <p className="mt-1 text-sm text-red-400">{formErrors.scheduled_date}</p>}
                </div>

                {/* Estimated Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Estimated Hours <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    name="estimated_hours"
                    value={formData.estimated_hours}
                    onChange={handleChange}
                    min="0.01"
                    step="0.01"
                    max="99.99"
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.estimated_hours ? 'border-red-500' : 'border-gray-600'
                      }`}
                    placeholder="e.g., 2.5"
                    disabled={localIsSubmitting}
                  />
                  {formErrors.estimated_hours && <p className="mt-1 text-sm text-red-400">{formErrors.estimated_hours}</p>}
                </div>
              </div>

              {/* Description (full width) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Optional description"
                  disabled={localIsSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Footer buttons */}
          <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={localIsSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg border border-blue-600 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              disabled={localIsSubmitting || isUpdating}
            >
              {localIsSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === 'duplicate' ? 'Create Duplicate' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
  return createPortal(modalContent, document.body);
};
